import React, { useState, useEffect } from 'react';
import { Search, ChevronLeft, ChevronRight, CheckCircle, XCircle, MapPin, Shield, ShieldOff, Trash2 } from 'lucide-react';
import { fetchFuelStations, fetchFuelStationBrands, updateFuelStation, bulkVerifyFuelStations, deleteFuelStation, fetchCountriesForDropdown } from '../services/api.ts';
import { FuelStation } from '../types/fuel.ts';
import './FuelStations.css';

const FuelStations: React.FC = () => {
  const [stations, setStations] = useState<FuelStation[]>([]);
  const [countries, setCountries] = useState<{ id: number; name: string }[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBrand, setFilterBrand] = useState<string>('');
  const [filterVerified, setFilterVerified] = useState<string>('all');
  const [filterCountry, setFilterCountry] = useState<number | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const perPage = 20;

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [countriesData, brandsData] = await Promise.all([
          fetchCountriesForDropdown(),
          fetchFuelStationBrands(),
        ]);
        setCountries(countriesData || []);
        setBrands(brandsData || []);
      } catch (err) {
        console.error('Error loading dropdowns:', err);
      }
    };
    loadDropdowns();
  }, []);

  useEffect(() => {
    loadStations();
  }, [currentPage, filterBrand, filterVerified, filterCountry]);

  const loadStations = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * perPage;
      const verified = filterVerified === 'all' ? undefined : filterVerified === 'verified';
      const data = await fetchFuelStations(
        offset, perPage, filterCountry, filterBrand || undefined, verified, searchTerm || undefined
      );
      setStations(data.items || []);
      setTotal(data.total || 0);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stations');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadStations();
  };

  const handleToggleVerified = async (station: FuelStation) => {
    try {
      setActionLoading(station.id);
      await updateFuelStation(station.id, { is_verified: !station.is_verified });
      setStations(prev => prev.map(s => s.id === station.id ? { ...s, is_verified: !s.is_verified } : s));
    } catch (err) {
      console.error('Error updating station:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleActive = async (station: FuelStation) => {
    try {
      setActionLoading(station.id);
      await updateFuelStation(station.id, { is_active: !station.is_active });
      setStations(prev => prev.map(s => s.id === station.id ? { ...s, is_active: !s.is_active } : s));
    } catch (err) {
      console.error('Error updating station:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkVerify = async () => {
    if (selectedIds.size === 0) return;
    try {
      setActionLoading(-1);
      await bulkVerifyFuelStations(Array.from(selectedIds));
      setStations(prev => prev.map(s => selectedIds.has(s.id) ? { ...s, is_verified: true } : s));
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Error bulk verifying:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleDelete = async (station: FuelStation) => {
    if (!window.confirm(`Delete "${station.name}"? This will permanently remove the station and all its price data.`)) return;
    try {
      setActionLoading(station.id);
      await deleteFuelStation(station.id);
      setStations(prev => prev.filter(s => s.id !== station.id));
      setTotal(prev => prev - 1);
    } catch (err) {
      console.error('Error deleting station:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === stations.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(stations.map(s => s.id)));
    }
  };

  const totalPages = Math.ceil(total / perPage);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading && stations.length === 0) {
    return (
      <div className="fuel-stations-page">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading stations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fuel-stations-page">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="fuel-stations-page">
      <div className="page-header">
        <h2><MapPin size={24} /> Fuel Stations</h2>
        <div className="page-actions">
          <form onSubmit={handleSearch} className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search stations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </form>
        </div>
      </div>

      <div className="filter-section">
        <div className="filter-group">
          <label>Country:</label>
          <select
            value={filterCountry ?? ''}
            onChange={(e) => { setFilterCountry(e.target.value ? parseInt(e.target.value) : undefined); setCurrentPage(1); }}
          >
            <option value="">All Countries</option>
            {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Brand:</label>
          <select value={filterBrand} onChange={(e) => { setFilterBrand(e.target.value); setCurrentPage(1); }}>
            <option value="">All Brands</option>
            {brands.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Verified:</label>
          <select value={filterVerified} onChange={(e) => { setFilterVerified(e.target.value); setCurrentPage(1); }}>
            <option value="all">All</option>
            <option value="verified">Verified</option>
            <option value="unverified">Unverified</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Showing:</label>
          <span style={{ color: '#6366f1', fontWeight: 600 }}>
            {stations.length} of {total} stations
          </span>
        </div>
        {selectedIds.size > 0 && (
          <button className="bulk-verify-btn" onClick={handleBulkVerify} disabled={actionLoading === -1}>
            <Shield size={16} />
            Verify {selectedIds.size} Selected
          </button>
        )}
      </div>

      {stations.length === 0 ? (
        <div className="empty-state"><p>No stations found.</p></div>
      ) : (
        <>
          <div className="fuel-table-container">
            <table className="fuel-table">
              <thead>
                <tr>
                  <th className="check-col">
                    <input type="checkbox" checked={selectedIds.size === stations.length && stations.length > 0} onChange={toggleSelectAll} />
                  </th>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Brand</th>
                  <th>Suburb</th>
                  <th>Prices</th>
                  <th>Last Report</th>
                  <th>Verified</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {stations.map(station => (
                  <tr key={station.id} className={!station.is_active ? 'row-inactive' : ''}>
                    <td className="check-col">
                      <input type="checkbox" checked={selectedIds.has(station.id)} onChange={() => toggleSelect(station.id)} />
                    </td>
                    <td className="id-cell">{station.id}</td>
                    <td className="name-cell">
                      <span className="station-name">{station.name}</span>
                      <span className="station-address">{station.address}</span>
                    </td>
                    <td>{station.brand}</td>
                    <td>{station.suburb || '—'}</td>
                    <td className="center-cell">{station.active_prices_count ?? 0}</td>
                    <td>{formatDate(station.last_report_at)}</td>
                    <td className="center-cell">
                      <span className={`status-badge ${station.is_verified ? 'verified' : 'unverified'}`}>
                        {station.is_verified ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="center-cell">
                      <span className={`status-badge ${station.is_active ? 'active' : 'inactive'}`}>
                        {station.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button
                        className={`action-btn ${station.is_verified ? 'warn' : 'success'}`}
                        onClick={() => handleToggleVerified(station)}
                        disabled={actionLoading === station.id}
                        title={station.is_verified ? 'Unverify' : 'Verify'}
                      >
                        {station.is_verified ? <ShieldOff size={15} /> : <Shield size={15} />}
                      </button>
                      <button
                        className={`action-btn ${station.is_active ? 'danger' : 'success'}`}
                        onClick={() => handleToggleActive(station)}
                        disabled={actionLoading === station.id}
                        title={station.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {station.is_active ? <XCircle size={15} /> : <CheckCircle size={15} />}
                      </button>
                      <button
                        className="action-btn danger"
                        onClick={() => handleDelete(station)}
                        disabled={actionLoading === station.id}
                        title="Delete station"
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button className="pagination-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                <ChevronLeft size={18} /> Previous
              </button>
              <div className="pagination-info">Page {currentPage} of {totalPages}</div>
              <button className="pagination-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                Next <ChevronRight size={18} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FuelStations;
